import { AdvancedMarker, Map } from "@vis.gl/react-google-maps";
import { AlertTriangle } from "@untitledui/icons";
import { useGoogleMaps } from "@/providers/google-maps-provider";
import { cx } from "@/utils/cx";
import type { AddressDto } from "@fp/admin-api-client/types/addressDto.js";

export const formatAddress = (address: AddressDto | undefined) => {
  if (!address) {
    return null;
  }
  const { addressLine1, addressLine2, city, state, zip } = address;
  return [addressLine1, addressLine2, city, state, zip].filter(Boolean).join(", ");
};

export const AddressLine = ({ address }: { address: AddressDto }) => {
  const { addressLine1, addressLine2, city, state, zip } = address;

  if (!addressLine1 && !addressLine2 && !city && !state && !zip) {
    return null;
  }

  const street = [addressLine1, addressLine2].filter(Boolean).join(" ");
  const stateZip = [state, zip].filter(Boolean).join(" ");
  const cityStateZip = [city, stateZip].filter(Boolean).join(", ");

  if (!street) {
    return <p className="text-sm text-quaternary">{cityStateZip}</p>;
  }

  return (
    <div className="flex flex-col">
      <p className="text-sm text-quaternary">{street}</p>
      <p className="text-sm text-quaternary">{cityStateZip}</p>
    </div>
  );
};

export const AddressMap = ({ address }: { address: AddressDto }) => {
  const googleMaps = useGoogleMaps();

  if (address.geocodeFailed) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-warning-50 border border-warning-300 px-3 py-2">
        <AlertTriangle className="size-4 text-warning-600 shrink-0" />
        <span className="text-sm text-warning-700">
          We couldn't locate this address on a map. Double-check it for typos.
        </span>
      </div>
    );
  }

  if (!googleMaps || address.latitude == null || address.longitude == null) {
    return null;
  }

  const position = { lat: address.latitude, lng: address.longitude };

  return (
    <div className="relative isolate block w-full h-[200px] rounded-md bg-gray-100 overflow-hidden">
      <Map
        mapId={googleMaps.mapId}
        defaultCenter={position}
        defaultZoom={13}
        gestureHandling="cooperative"
        disableDefaultUI={true}
        zoomControl={true}
      >
        <AdvancedMarker position={position} />
      </Map>
    </div>
  );
};

const isSafari = () => /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(navigator.userAgent);

const buildMapsUrl = (address: AddressDto) => {
  const street = [address.addressLine1, address.addressLine2].filter(Boolean).join(" ");
  const addressQuery = `${street}, ${address.city}, ${address.state}, ${address.zip}`;

  if (isSafari()) {
    return `https://maps.apple.com/?ll=${address.latitude},${address.longitude}&q=${encodeURIComponent(addressQuery)}`;
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${address.latitude},${address.longitude}`)}`;
};

export const AddressLink = ({
  address,
  className,
}: {
  address: AddressDto;
  className?: string;
}) => {
  if (address.latitude == null || address.longitude == null) {
    return null;
  }

  const url = buildMapsUrl(address);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cx("text-sm text-blue-500", className)}
    >
      Open in Maps
    </a>
  );
};